import React, { useEffect, useState } from 'react'
import {
    Table,
    CardBody,
    Accordion,
    AccordionItem,
    AccordionHeader,
    AccordionBody,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Button
} from 'reactstrap'
import { API_URL } from './utils/Constants/URLS'
import { useParams } from 'react-router-dom'

// --- Helper: format money ---
const money = n =>
    (Number.isFinite(+n) ? Number(n) : 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })

// helper to derive consistent e-way bill id
const getEWayBillId = (ewb, idx) => String(ewb._id ?? ewb.id ?? `ewb-${idx}`)

const EWayBill = () => {
    const token =
        typeof window !== 'undefined' ? localStorage.getItem('token') : null

    const { storeid } = useParams()

    const [eWayBillData, setEWayBillData] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [openId, setOpenId] = useState('')

    // Modal states
    const [partBModal, setPartBModal] = useState(false)
    const [transporterModal, setTransporterModal] = useState(false)
    const [cancelModal, setCancelModal] = useState(false)
    const [selectedEwayBill, setSelectedEwayBill] = useState(null)

    // Form data states
    const [partBData, setPartBData] = useState({
        vehicleNo: '',
        fromPlace: '',
        fromState: '',
        reasonCode: '1',
        reasonRem: '',
        transMode: '1',
        transDocNo: '',
        transDocDate: ''
    })
    const [transporterData, setTransporterData] = useState({
        transporterId: ''
    })
    const [cancelData, setCancelData] = useState({
        cancelRsnCode: '1',
        cancelRmrk: ''
    })

    // Loading states for API calls
    const [partBLoading, setPartBLoading] = useState(false)
    const [transporterLoading, setTransporterLoading] = useState(false)
    const [cancelLoading, setCancelLoading] = useState(false)

    useEffect(() => {
        const fetchEWayBills = async () => {
            setLoading(true)
            setError(null)
            try {
                const url = `${API_URL}ewaybills?pagination=false`
                const res = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: token ? `Bearer ${token}` : ''
                    }
                })

                if (!res.ok) {
                    const txt = await res.text()
                    throw new Error(txt || `Status ${res.status}`)
                }

                const json = await res.json()

                const docs =
                    json?.message?.docs ||
                    json?.data?.docs ||
                    json?.docs ||
                    (Array.isArray(json) ? json : []) ||
                    []

                setEWayBillData(docs)
            } catch (err) {
                console.error('Fetch e-way bills error:', err)
                setError(err.message || 'Failed to fetch e-way bills')
                setEWayBillData([])
            } finally {
                setLoading(false)
            }
        }

        fetchEWayBills()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, storeid])

    const toggleAccordion = id => {
        setOpenId(prev => (prev === id ? '' : id))
    }

    const handlePrintPDF = (e, pdfUrl) => {
        e.stopPropagation() // Prevent accordion from toggling
        if (pdfUrl) {
            const fullUrl = `https://my.gstzen.in${pdfUrl}`
            window.open(fullUrl, '_blank')
        } else {
            alert('PDF URL not available')
        }
    }

    // Modal handlers
    const openPartBModal = (ewb) => {
        setSelectedEwayBill(ewb)
        const payload = ewb?.gstzenRequestPayload || {}
        setPartBData({
            vehicleNo: payload.vehicleNo || '',
            fromPlace: payload.fromPlace || '',
            fromState: payload.fromStateCode || '',
            reasonCode: '1',
            reasonRem: '',
            transMode: payload.transMode || '1',
            transDocNo: payload.transDocNo || '',
            transDocDate: payload.transDocDate || ''
        })
        setPartBModal(true)
    }

    const openTransporterModal = (ewb) => {
        setSelectedEwayBill(ewb)
        const payload = ewb?.gstzenRequestPayload || {}
        setTransporterData({
            transporterId: payload.transporterId || ''
        })
        setTransporterModal(true)
    }

    const openCancelModal = (ewb) => {
        setSelectedEwayBill(ewb)
        setCancelData({
            cancelRsnCode: '1',
            cancelRmrk: ''
        })
        setCancelModal(true)
    }

    const closeAllModals = () => {
        setPartBModal(false)
        setTransporterModal(false)
        setCancelModal(false)
        setSelectedEwayBill(null)
    }

    // API handlers
    const handleUpdatePartB = async () => {
        if (!selectedEwayBill?._id) return

        // Validate transport document fields for non-road transport
        if (partBData.transMode !== '1') {
            if (!partBData.transDocNo || !partBData.transDocDate) {
                alert('Transport Document Number and Date are mandatory for non-road transport modes!')
                return
            }
        }

        setPartBLoading(true)
        try {
            const response = await fetch(`${API_URL}ewaybills/update-partb`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token ? `Bearer ${token}` : ''
                },
                body: JSON.stringify({
                    ewayBillId: selectedEwayBill._id,
                    vehicleNo: partBData.vehicleNo,
                    fromPlace: partBData.fromPlace,
                    fromState: parseInt(partBData.fromState),
                    reasonCode: partBData.reasonCode,
                    reasonRem: partBData.reasonRem,
                    transMode: partBData.transMode,
                    transDocNo: partBData.transDocNo,
                    transDocDate: partBData.transDocDate
                })
            })

            const result = await response.json()

            if (!response.ok) {
                throw new Error(result.message || 'Failed to update Part B')
            }

            alert('Part B updated successfully!')
            closeAllModals()
            // Refresh the e-way bills data
            window.location.reload()
        } catch (err) {
            console.error('Update Part B error:', err)
            alert(err.message || 'Failed to update Part B')
        } finally {
            setPartBLoading(false)
        }
    }

    const handleUpdateTransporter = async () => {
        if (!selectedEwayBill?._id) return

        setTransporterLoading(true)
        try {
            const response = await fetch(`${API_URL}ewaybills/update-transporter`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token ? `Bearer ${token}` : ''
                },
                body: JSON.stringify({
                    ewayBillId: selectedEwayBill._id,
                    transporterId: transporterData.transporterId
                })
            })

            const result = await response.json()

            if (!response.ok) {
                throw new Error(result.message || 'Failed to update transporter')
            }

            alert('Transporter updated successfully!')
            closeAllModals()
            // Refresh the e-way bills data
            window.location.reload()
        } catch (err) {
            console.error('Update Transporter error:', err)
            alert(err.message || 'Failed to update transporter')
        } finally {
            setTransporterLoading(false)
        }
    }

    const handleCancelEwayBill = async () => {
        if (!selectedEwayBill?._id) return

        if (!window.confirm('Are you sure you want to cancel this E-Way Bill?')) {
            return
        }

        setCancelLoading(true)
        try {
            const response = await fetch(`${API_URL}ewaybills/cancel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token ? `Bearer ${token}` : ''
                },
                body: JSON.stringify({
                    ewayBillId: selectedEwayBill._id,
                    cancelRsnCode: parseInt(cancelData.cancelRsnCode),
                    cancelRmrk: cancelData.cancelRmrk
                })
            })

            const result = await response.json()

            if (!response.ok) {
                throw new Error(result.message || 'Failed to cancel E-Way Bill')
            }

            alert('E-Way Bill cancelled successfully!')
            closeAllModals()
            // Refresh the e-way bills data
            window.location.reload()
        } catch (err) {
            console.error('Cancel E-Way Bill error:', err)
            alert(err.message || 'Failed to cancel E-Way Bill')
        } finally {
            setCancelLoading(false)
        }
    }

    const formatDateTime = iso => {
        if (!iso) return '-'
        try {
            const d = new Date(iso)
            return d.toLocaleString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            })
        } catch {
            return iso
        }
    }

    const renderEWayBillDetails = (ewb, id) => {
        const payload = ewb?.gstzenRequestPayload || {}
        const response = ewb?.gstzenResponse || {}
        const itemList = payload?.itemList || []

        return (
            <div className='row'>
                <div className='col-md-12'>
                    <Table className='table table-sm table-bordered mb-3' responsive>
                        <tbody>
                            <tr>
                                <th style={{ width: '25%' }}>E-Way Bill Number</th>
                                <td>{response?.ewayBillNo || '-'}</td>
                                <th style={{ width: '25%' }}>E-Way Bill Date</th>
                                <td>{response?.ewayBillDate || '-'}</td>
                            </tr>
                            <tr>
                                <th>Document Number</th>
                                <td>{payload?.docNo || '-'}</td>
                                <th>Document Date</th>
                                <td>{payload?.docDate || '-'}</td>
                            </tr>
                            <tr>
                                <th>Document Type</th>
                                <td>{payload?.docType || '-'}</td>
                                <th>Transaction Type</th>
                                <td>{payload?.transactionType || '-'}</td>
                            </tr>
                            <tr>
                                <th>Supply Type</th>
                                <td>{payload?.supplyType || '-'}</td>
                                <th>Sub Supply Type</th>
                                <td>{payload?.subSupplyType || '-'}</td>
                            </tr>
                            <tr>
                                <th>From GSTIN</th>
                                <td>{payload?.fromGstin || '-'}</td>
                                <th>To GSTIN</th>
                                <td>{payload?.toGstin || '-'}</td>
                            </tr>
                            <tr>
                                <th>From Trade Name</th>
                                <td>{payload?.fromTrdName || '-'}</td>
                                <th>To Trade Name</th>
                                <td>{payload?.toTrdName || '-'}</td>
                            </tr>
                            <tr>
                                <th>From Address</th>
                                <td colSpan='3'>
                                    {[payload?.fromAddr1, payload?.fromAddr2]
                                        .filter(Boolean)
                                        .join(', ') || '-'}
                                </td>
                            </tr>
                            <tr>
                                <th>To Address</th>
                                <td colSpan='3'>
                                    {[payload?.toAddr1, payload?.toAddr2]
                                        .filter(Boolean)
                                        .join(', ') || '-'}
                                </td>
                            </tr>
                            <tr>
                                <th>From Place</th>
                                <td>{payload?.fromPlace || '-'}</td>
                                <th>To Place</th>
                                <td>{payload?.toPlace || '-'}</td>
                            </tr>
                            <tr>
                                <th>From Pincode</th>
                                <td>{payload?.fromPincode || '-'}</td>
                                <th>To Pincode</th>
                                <td>{payload?.toPincode || '-'}</td>
                            </tr>
                            <tr>
                                <th>From State Code</th>
                                <td>{payload?.fromStateCode || '-'}</td>
                                <th>To State Code</th>
                                <td>{payload?.toStateCode || '-'}</td>
                            </tr>
                            <tr>
                                <th>Total Value</th>
                                <td>₹ {money(payload?.totalValue)}</td>
                                <th>Total Invoice Value</th>
                                <td>₹ {money(payload?.totInvValue)}</td>
                            </tr>
                            <tr>
                                <th>CGST Value</th>
                                <td>₹ {money(payload?.cgstValue)}</td>
                                <th>SGST Value</th>
                                <td>₹ {money(payload?.sgstValue)}</td>
                            </tr>
                            <tr>
                                <th>IGST Value</th>
                                <td>₹ {money(payload?.igstValue)}</td>
                                <th>Cess Value</th>
                                <td>₹ {money(payload?.cessValue)}</td>
                            </tr>
                            <tr>
                                <th>Transportation Mode</th>
                                <td>{payload?.transMode || '-'}</td>
                                <th>Transportation Distance</th>
                                <td>{payload?.transDistance || response?.Distance || '-'} KM</td>
                            </tr>
                            <tr>
                                <th>Transporter ID</th>
                                <td>{payload?.transporterId || '-'}</td>
                                <th>Transporter Name</th>
                                <td>{payload?.transporterName || '-'}</td>
                            </tr>
                            <tr>
                                <th>Vehicle Details</th>
                                <td>{payload?.vehicleNo || '-'}</td>
                                
                            </tr>
                            <tr>
                                <th>Valid Upto</th>
                                <td colSpan='3'>{response?.validUpto || '-'}</td>
                            </tr>
                            <tr>
                                <th>Status</th>
                                <td colSpan='3'>
                                    <span
                                        className={`badge ${ewb?.status === 'generated'
                                            ? 'bg-success'
                                            : ewb?.status === 'cancelled'
                                                ? 'bg-danger'
                                                : 'bg-secondary'
                                            }`}
                                    >
                                        {ewb?.status?.toUpperCase() || 'N/A'}
                                    </span>
                                </td>
                            </tr>
                            {response?.message && (
                                <tr>
                                    <th>Message</th>
                                    <td colSpan='3'>{response.message}</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    {/* Item Details Table */}
                    {itemList && itemList.length > 0 && (
                        <>
                            <h5 className='mb-3 mt-3 fw-bold'>Item Details</h5>
                            <Table
                                className='table table-sm table-bordered mb-3'
                                responsive
                            >
                                <thead className='table-light'>
                                    <tr>
                                        <th style={{ width: 50 }}>SI</th>
                                        <th>Product Name</th>
                                        <th>Description</th>
                                        <th style={{ width: 100 }}>HSN Code</th>
                                        <th style={{ width: 90 }}>Quantity</th>
                                        <th style={{ width: 90 }}>Unit</th>
                                        <th style={{ width: 120 }}>Taxable Amount</th>
                                        <th style={{ width: 90 }}>CGST Rate</th>
                                        <th style={{ width: 90 }}>SGST Rate</th>
                                        <th style={{ width: 90 }}>IGST Rate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {itemList.map((item, i) => (
                                        <tr key={i}>
                                            <td>{i + 1}</td>
                                            <td>{item?.productName || '-'}</td>
                                            <td>{item?.productDesc || '-'}</td>
                                            <td>{item?.hsnCode || '-'}</td>
                                            <td>{item?.quantity || 0}</td>
                                            <td>{item?.qtyUnit || '-'}</td>
                                            <td>₹ {money(item?.taxableAmount)}</td>
                                            <td>{item?.cgstRate || 0}%</td>
                                            <td>{item?.sgstRate || 0}%</td>
                                            <td>{item?.igstRate || 0}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            {response?.EWayBillPdfUrl && (
                                <div className='d-flex justify-content-end gap-2 mt-2'>
                                    <button
                                        className='btn btn-info btn-sm'
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            openPartBModal(ewb)
                                        }}
                                        title='Update Part B'
                                    >
                                        <i className='fa fa-edit'></i> Update Part B
                                    </button>
                                    <button
                                        className='btn btn-warning btn-sm'
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            openTransporterModal(ewb)
                                        }}
                                        title='Update Transporter'
                                    >
                                        <i className='fa fa-truck'></i> Update Transporter
                                    </button>
                                    <button
                                        className='btn btn-danger btn-sm'
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            openCancelModal(ewb)
                                        }}
                                        title='Cancel E-Way Bill'
                                    >
                                        <i className='fa fa-times'></i> Cancel
                                    </button>
                                    <button
                                        className='btn btn-primary btn-sm'
                                        onClick={(e) => handlePrintPDF(e, response.EWayBillPdfUrl)}
                                        title='Print E-Way Bill PDF'
                                    >
                                        <i className='fa fa-print'></i> Print E-Way Bill
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        )
    }

    if (loading) {
        return (
            <div className='card shadow-sm'>
                <CardBody className='p-4 text-center'>Loading e-way bills...</CardBody>
            </div>
        )
    }

    if (error) {
        return (
            <div className='card shadow-sm'>
                <CardBody className='p-4'>
                    <div className='alert alert-danger'>Error: {error}</div>
                </CardBody>
            </div>
        )
    }

    if (!eWayBillData || eWayBillData.length === 0) {
        return (
            <div className='card shadow-sm'>
                <CardBody className='p-4 text-center'>No e-way bills found.</CardBody>
            </div>
        )
    }

    return (
        <div className='card shadow-sm'>
            <CardBody className='p-4'>
                <div className='d-flex justify-content-between align-items-center mb-3'>
                    <h5 className='mb-0 ml-1'>E-Way Bills</h5>
                    <div className='text-muted small'>
                        Total E-Way Bills: <strong>{eWayBillData.length}</strong>
                    </div>
                </div>

                <Accordion
                    open={openId || ''}
                    toggle={toggleAccordion}
                    id='ewayBillAccordion'
                >
                    {eWayBillData.map((ewb, idx) => {
                        const id = getEWayBillId(ewb, idx)
                        const createdAt = ewb.createdAt ?? ewb.updatedAt ?? null
                        const response = ewb?.gstzenResponse || {}
                        const payload = ewb?.gstzenRequestPayload || {}

                        return (
                            <AccordionItem key={id}>
                                <AccordionHeader targetId={id}>
                                    <div className='d-flex flex-column flex-md-row align-items-start gap-2 w-100'>
                                        <div className='me-auto'>
                                            <strong>E-Way Bill:</strong> {response?.ewayBillNo || 'N/A'}{' '}
                                            <span className='text-muted'>
                                                | Doc: {payload?.docNo || 'N/A'}
                                            </span>
                                        </div>
                                        <div className='text-muted small'>
                                            Created: {formatDateTime(createdAt)}
                                        </div>
                                        <div className='ms-md-3'>
                                            <span className='badge bg-light text-dark'>
                                                Value: ₹ {money(payload?.totInvValue)}
                                            </span>
                                        </div>
                                    </div>
                                </AccordionHeader>

                                <AccordionBody accordionId={id}>
                                    {renderEWayBillDetails(ewb, id)}
                                </AccordionBody>
                            </AccordionItem>
                        )
                    })}
                </Accordion>
            </CardBody>

            {/* Update Part B Modal */}
            <Modal isOpen={partBModal} toggle={closeAllModals} size='lg'>
                <ModalHeader toggle={closeAllModals}>Update Part B</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label for='vehicleNo'>Vehicle Details <span className='text-danger'>*</span></Label>
                            <Input
                                type='text'
                                id='vehicleNo'
                                value={partBData.vehicleNo}
                                onChange={(e) =>
                                    setPartBData({ ...partBData, vehicleNo: e.target.value })
                                }
                                placeholder='Enter vehicle details'
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for='fromPlace'>From Place <span className='text-danger'>*</span></Label>
                            <Input
                                type='text'
                                id='fromPlace'
                                value={partBData.fromPlace}
                                onChange={(e) =>
                                    setPartBData({ ...partBData, fromPlace: e.target.value })
                                }
                                placeholder='Enter from place'
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for='fromState'>From State Code <span className='text-danger'>*</span></Label>
                            <Input
                                type='number'
                                id='fromState'
                                value={partBData.fromState}
                                onChange={(e) =>
                                    setPartBData({ ...partBData, fromState: e.target.value })
                                }
                                placeholder='Enter state code (e.g., 29)'
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for='reasonCode'>Reason Code <span className='text-danger'>*</span></Label>
                            <Input
                                type='select'
                                id='reasonCode'
                                value={partBData.reasonCode}
                                onChange={(e) =>
                                    setPartBData({ ...partBData, reasonCode: e.target.value })
                                }
                            >
                                <option value='1'>Due to Break Down</option>
                                <option value='2'>Due to Transhipment</option>
                                <option value='3'>Others</option>
                                <option value='4'>First Time</option>
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label for='reasonRem'>Reason Remark</Label>
                            <Input
                                type='textarea'
                                id='reasonRem'
                                value={partBData.reasonRem}
                                onChange={(e) =>
                                    setPartBData({ ...partBData, reasonRem: e.target.value })
                                }
                                placeholder='Enter reason remark'
                                rows={3}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for='transMode'>Transportation Mode <span className='text-danger'>*</span></Label>
                            <Input
                                type='select'
                                id='transMode'
                                value={partBData.transMode}
                                onChange={(e) =>
                                    setPartBData({ ...partBData, transMode: e.target.value })
                                }
                            >
                                <option value='1'>Road</option>
                                <option value='2'>Rail</option>
                                <option value='3'>Air</option>
                                <option value='4'>Ship</option>
                            </Input>
                        </FormGroup>
                        {partBData.transMode !== '1' && (
                            <>
                                <FormGroup>
                                    <Label for='transDocNo'>Transport Document Number <span className='text-danger'>*</span></Label>
                                    <Input
                                        type='text'
                                        id='transDocNo'
                                        value={partBData.transDocNo}
                                        onChange={(e) =>
                                            setPartBData({ ...partBData, transDocNo: e.target.value })
                                        }
                                        placeholder='Enter transport document number'
                                        required
                                    />
                                    <small className='text-muted'>Required for Rail, Air, and Ship transport</small>
                                </FormGroup>
                                <FormGroup>
                                    <Label for='transDocDate'>Transport Document Date <span className='text-danger'>*</span></Label>
                                    <Input
                                        type='date'
                                        id='transDocDate'
                                        value={partBData.transDocDate}
                                        onChange={(e) =>
                                            setPartBData({ ...partBData, transDocDate: e.target.value })
                                        }
                                        required
                                    />
                                    <small className='text-muted'>Required for Rail, Air, and Ship transport</small>
                                </FormGroup>
                            </>
                        )}
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color='secondary' onClick={closeAllModals}>
                        Cancel
                    </Button>
                    <Button
                        color='primary'
                        onClick={handleUpdatePartB}
                        disabled={
                            partBLoading ||
                            !partBData.vehicleNo ||
                            !partBData.fromPlace ||
                            !partBData.fromState ||
                            (partBData.transMode !== '1' && (!partBData.transDocNo || !partBData.transDocDate))
                        }
                    >
                        {partBLoading ? 'Updating...' : 'Update Part B'}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Update Transporter Modal */}
            <Modal isOpen={transporterModal} toggle={closeAllModals}>
                <ModalHeader toggle={closeAllModals}>Update Transporter</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label for='transporterId'>Transporter ID (GSTIN) *</Label>
                            <Input
                                type='text'
                                id='transporterId'
                                value={transporterData.transporterId}
                                onChange={(e) =>
                                    setTransporterData({ ...transporterData, transporterId: e.target.value })
                                }
                                placeholder='Enter transporter GSTIN'
                                required
                            />
                            <small className='text-muted'>Enter 15-digit GSTIN of the transporter</small>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color='secondary' onClick={closeAllModals}>
                        Cancel
                    </Button>
                    <Button
                        color='primary'
                        onClick={handleUpdateTransporter}
                        disabled={transporterLoading || !transporterData.transporterId}
                    >
                        {transporterLoading ? 'Updating...' : 'Update Transporter'}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Cancel E-Way Bill Modal */}
            <Modal isOpen={cancelModal} toggle={closeAllModals}>
                <ModalHeader toggle={closeAllModals}>Cancel E-Way Bill</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label for='cancelRsnCode'>Cancel Reason Code *</Label>
                            <Input
                                type='select'
                                id='cancelRsnCode'
                                value={cancelData.cancelRsnCode}
                                onChange={(e) =>
                                    setCancelData({ ...cancelData, cancelRsnCode: e.target.value })
                                }
                            >
                                <option value='1'>Duplicate</option>
                                <option value='2'>Order Cancelled</option>
                                <option value='3'>Data Entry Mistake</option>
                                <option value='4'>Others</option>
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label for='cancelRmrk'>Cancel Remark *</Label>
                            <Input
                                type='textarea'
                                id='cancelRmrk'
                                value={cancelData.cancelRmrk}
                                onChange={(e) =>
                                    setCancelData({ ...cancelData, cancelRmrk: e.target.value })
                                }
                                placeholder='Enter cancellation remark'
                                rows={3}
                                required
                            />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color='secondary' onClick={closeAllModals}>
                        Close
                    </Button>
                    <Button
                        color='danger'
                        onClick={handleCancelEwayBill}
                        disabled={cancelLoading || !cancelData.cancelRmrk}
                    >
                        {cancelLoading ? 'Cancelling...' : 'Cancel E-Way Bill'}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    )
}

export default EWayBill
